import { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { getAccess } from '../services/authService';
import DocumentUpload from '../components/pulse/DocumentUpload';
import BrandVoice from '../components/pulse/BrandVoice';
import PromptConfig from '../components/pulse/PromptConfig';
import BrandIdentity from '../components/pulse/BrandIdentity';
import IntegrationsConfig from '../components/pulse/IntegrationsConfig';
import HumanSupportEmail from '../components/pulse/HumanSupportEmail';
import WebsiteTrain from '../components/pulse/WebsiteTrain';
import ChatPreview from '../components/pulse/ChatPreview';
import UsageLimits from '../components/pulse/UsageLimits';

const configs = [
  {
    id: 'brand-voice',
    label: 'Brand Voice',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
      </svg>
    ),
    desc: 'Define your tone, language style, and personality for the AI.',
    detail: 'Set the personality and communication style of your AI agent. Choose between formal, friendly, concise, or detailed — and give it a name that fits your brand.',
  },
  {
    id: 'brand-identity',
    label: 'Brand Identity',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="13.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="10.5" r="2.5"/><circle cx="8.5" cy="7.5" r="2.5"/><circle cx="6.5" cy="12.5" r="2.5"/><path d="M12 20v-5m0 0c-2 0-5-1-5-4m5 4c2 0 5-1 5-4"/>
      </svg>
    ),
    desc: 'Customise the chat widget colours, logo, and appearance.',
    detail: 'Match the Pulse chat widget to your brand. Upload your logo, set primary colours, choose widget position, and preview the embed before going live.',
  },
  {
    id: 'prompt',
    label: 'Prompt',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>
      </svg>
    ),
    desc: 'Write the system prompt that controls how your AI agent behaves.',
    detail: 'Define the core instructions for your AI agent — its role, constraints, tone, and how it should handle edge cases. The system prompt is the foundation of every conversation.',
  },
  {
    id: 'human-support',
    label: 'Human Support',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    desc: 'Set the email address for human agent escalations.',
  },
  {
    id: 'website',
    label: 'Train on Website',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
    desc: 'Enter your website URL so Pulse can crawl and learn from your content.',
    detail: null,
  },
  {
    id: 'documents',
    label: 'Train on Documents',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/>
      </svg>
    ),
    desc: 'Upload PDFs, docs, or paste URLs so Pulse learns from your content.',
    detail: null,
  },
  {
    id: 'preview',
    label: 'Live Preview',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    desc: 'Chat with your AI agent in real time using your saved configuration.',
    detail: null,
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="2" width="8" height="8" rx="1"/><rect x="2" y="14" width="8" height="8" rx="1"/><path d="M22 14h-4v4m0 4v-4m0 0h-4"/>
      </svg>
    ),
    desc: 'Connect Pulse to your CRM, helpdesk, Slack, and more.',
    detail: 'Integrate Pulse with tools your team already uses — Slack, HubSpot, Intercom, Zendesk, and more. Route leads and escalations automatically.',
  },
  {
    id: 'limits',
    label: 'Limits',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    desc: 'View your monthly usage across messages, website, and document trainings.',
  },
  {
    id: 'plans',
    label: 'Manage Plan',
    href: '/dashboard/plans',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="6" height="18" rx="1"/><rect x="9" y="3" width="6" height="18" rx="1"/><rect x="16" y="3" width="6" height="18" rx="1"/>
      </svg>
    ),
    desc: 'View and upgrade your current plan.',
  },
];

export default function PulseDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(configs[0].id);
  const isMobileLayout = window.matchMedia('(max-width: 640px)').matches;
  const sidebarRef = useRef(null);

  const activeConfig = configs.find(c => c.id === active);

  useEffect(() => {
    if (!user) return;
    getAccess().then(({ emailVerified }) => {
      if (!emailVerified) navigate('/dashboard', { replace: true });
    }).catch(() => {});
  }, [user, navigate]);

  // Manual horizontal scroll for iOS Safari — CSS overflow-x is unreliable there
  useEffect(() => {
    if (!isMobileLayout) return;
    const el = sidebarRef.current;
    if (!el) return;
    let startX = 0, startY = 0, startScroll = 0, tracking = false;
    function onTouchStart(e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startScroll = el.scrollLeft;
      tracking = true;
    }
    function onTouchMove(e) {
      if (!tracking) return;
      const dx = startX - e.touches[0].clientX;
      const dy = startY - e.touches[0].clientY;
      if (Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault();
        el.scrollLeft = startScroll + dx;
      } else {
        tracking = false;
      }
    }
    function onTouchEnd() { tracking = false; }
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobileLayout]);

  useLayoutEffect(() => {
    const isMobile = window.matchMedia('(pointer: coarse)').matches;
    if (active === 'preview' && isMobile) {
      const frames = [...document.querySelectorAll('body > iframe')];
      frames.forEach(f => { f.style.display = 'none'; });
      return () => frames.forEach(f => { f.style.display = ''; });
    }
  }, [active]);

  return (
    <div className="dark-scrollbar" style={{ background: '#0f172a', color: '#e2e8f0', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 28px', height: 64, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/dashboard" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: '#94a3b8', fontSize: 13.5, fontWeight: 500, textDecoration: 'none',
            transition: 'color .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color='#e2e8f0'}
            onMouseLeave={e => e.currentTarget.style.color='#94a3b8'}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 7H2M6 3L2 7l4 4"/>
            </svg>
            Dashboard
          </Link>

          <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.12)' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg,#479EC3,#22D3EE)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 32 32" fill="none">
                <polyline points="4,16 8,16 10,9 13,23 16,12 19,20 22,16 28,16" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Pulse Dashboard</span>
          </div>

          <div style={{ flex: 1 }} />

          <div className="pd-user-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            {user?.companyName && (
              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b' }}>
                {user.companyName}
                {user.companyId && <span style={{ color: '#334155', fontWeight: 400, marginLeft: 6 }}>{user.companyId}</span>}
              </span>
            )}
            <span style={{ fontSize: 12, color: '#475569' }}>{user?.email}</span>
          </div>
          <div className="pd-divider" style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.1)' }} />
          <button onClick={logout} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 13.5, fontWeight: 600, color: '#64748b',
            display: 'flex', alignItems: 'center', gap: 5,
            transition: 'color .15s', whiteSpace: 'nowrap',
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
            onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span className="pd-signout-label">Sign Out</span>
          </button>
          <style>{`
            @media (max-width: 640px) {
              .pd-user-info { display: none !important; }
              .pd-divider   { display: none !important; }
              .pd-signout-label { display: none; }
              .pd-outer { padding: 20px 12px !important; }
              .pd-config-panel {
                flex-direction: column !important;
                min-height: 0 !important;
                max-height: none !important;
                overflow: visible !important;
              }
              .pd-sidebar {
                width: 100% !important;
                display: flex !important;
                flex-direction: row !important;
                border-right: none !important;
                border-bottom: 1px solid rgba(255,255,255,0.06) !important;
                padding: 6px 8px !important;
                overflow-x: auto !important;
                overflow-y: hidden !important;
                scrollbar-width: none !important;
                gap: 4px;
              }
              .pd-sidebar::-webkit-scrollbar { display: none; }
              .pd-sidebar-heading { display: none !important; }
              .pd-sidebar-btn {
                width: auto !important;
                flex-shrink: 0 !important;
                border-left: none !important;
                border-radius: 8px !important;
                padding: 8px 12px !important;
                font-size: 12.5px !important;
                white-space: nowrap !important;
              }
              .pd-sidebar-btn.pd-active {
                background: rgba(71,158,195,0.15) !important;
                color: #e2e8f0 !important;
              }
              .pd-sidebar-label { display: none; }
              .pd-content {
                padding: 24px 16px !important;
                overflow-y: visible !important;
              }
            }
          `}</style>
        </div>
      </div>

      <div className="pd-outer" style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px' }}>

        {/* Greeting */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          style={{ marginBottom: 40 }}>
          <p style={{ fontSize: 13, color: '#479EC3', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Pulse by Reano</p>
          <h1 style={{ fontSize: 'clamp(22px,3vw,32px)', fontWeight: 800, letterSpacing: '-.03em', marginBottom: 6 }}>
            Your AI Support Dashboard
          </h1>
          <p style={{ fontSize: 15, color: '#64748b' }}>Configure your AI agent below.</p>
        </motion.div>

        {/* Live Preview — full-screen overlay below header */}
        {active === 'preview' && (
          <motion.div
            key="preview-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed', top: 64, left: 0, right: 0, bottom: 0,
              background: '#0f172a', zIndex: 50,
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Preview top bar */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 24px', flexShrink: 0,
              background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(12px)',
              borderBottom: '1px solid rgba(71,158,195,0.12)',
            }}>
              <button onClick={() => setActive(configs[0].id)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, padding: 0,
                transition: 'color .15s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
                onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 5l-7 7 7 7"/>
                </svg>
                Back to Config
              </button>
              <span style={{ fontSize: 12, color: '#1e293b' }}>·</span>
              <span style={{ fontSize: 13, color: '#479EC3', fontWeight: 600 }}>Live Preview</span>
            </div>

            {/* Widget iframe fills remaining space */}
            <div style={{ flex: 1, minHeight: 0 }}>
              <ChatPreview />
            </div>
          </motion.div>
        )}

        {/* Config panel */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
          className="pd-config-panel"
          style={{
            background: 'rgba(30,41,59,0.6)', backdropFilter: 'blur(16px)',
            border: '1px solid rgba(71,158,195,0.15)', borderRadius: 16,
            display: 'flex', minHeight: 520, maxHeight: 520, overflow: 'hidden',
          }}>
          <>
          {/* Sidebar */}
          <div ref={sidebarRef} className="pd-sidebar" style={{
            width: 240, flexShrink: 0,
            borderRight: '1px solid rgba(255,255,255,0.06)',
            padding: '8px 0',
          }}>
            <p className="pd-sidebar-heading" style={{ fontSize: 11, fontWeight: 700, color: '#334155', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '16px 20px 8px' }}>Configuration</p>
            {configs.map(c => {
              const isActive = c.id === active;
              const btnStyle = {
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 20px', background: isActive ? 'rgba(71,158,195,0.1)' : 'transparent',
                border: 'none', borderLeft: `2px solid ${isActive ? '#479EC3' : 'transparent'}`,
                cursor: 'pointer', textAlign: 'left',
                color: isActive ? '#e2e8f0' : '#64748b',
                fontSize: 13.5, fontWeight: isActive ? 600 : 400,
                transition: 'background .15s, color .15s',
                textDecoration: 'none',
              };
              const inner = (
                <>
                  <span style={{ color: isActive ? '#479EC3' : '#475569', flexShrink: 0 }}>{c.icon}</span>
                  <span className="pd-sidebar-label">{c.label}</span>
                </>
              );
              return c.href ? (
                <Link key={c.id} to={c.href}
                  className="pd-sidebar-btn"
                  style={btnStyle}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94a3b8'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
                >
                  {inner}
                </Link>
              ) : (
                <button key={c.id} onClick={() => setActive(c.id)}
                  className={`pd-sidebar-btn${isActive ? ' pd-active' : ''}`}
                  style={btnStyle}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94a3b8'; } }}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; } }}
                >
                  {inner}
                </button>
              );
            })}
          </div>

          {/* Content panel */}
          <div className="pd-content" style={{ flex: 1, padding: '36px 40px', display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'auto' }}>
            <AnimatePresence mode="wait">
              <motion.div key={active}
                initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.18 }}
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(71,158,195,0.1)', border: '1px solid rgba(71,158,195,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#479EC3', flexShrink: 0 }}>
                    {activeConfig.icon}
                  </div>
                  <div>
                    <h2 style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9', marginBottom: 2 }}>{activeConfig.label}</h2>
                    <p style={{ fontSize: 13, color: '#64748b' }}>{activeConfig.desc}</p>
                  </div>
                </div>

                <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '20px 0' }} />

                {/* Per-section body */}
                {active === 'website' ? (
                  <WebsiteTrain />
                ) : active === 'documents' ? (
                  <DocumentUpload />
                ) : active === 'brand-voice' ? (
                  <BrandVoice />
                ) : active === 'prompt' ? (
                  <PromptConfig />
                ) : active === 'brand-identity' ? (
                  <BrandIdentity />
                ) : active === 'integrations' ? (
                  <IntegrationsConfig />
                ) : active === 'human-support' ? (
                  <HumanSupportEmail />
                ) : active === 'limits' ? (
                  <UsageLimits />
                ) : (
                  <>
                    <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 32 }}>{activeConfig.detail}</p>
                    <div style={{ marginTop: 'auto' }}>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '10px 18px', borderRadius: 10,
                        background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <span style={{ fontSize: 13, color: '#818cf8', fontWeight: 500 }}>Coming soon — contact <a href="mailto:support@reanoai.com" style={{ color: '#a5b4fc', textDecoration: 'none' }}>support@reanoai.com</a> for manual setup</span>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          </>

        </motion.div>

      </div>
    </div>
  );
}
